// ABOUTME: Props interface for custom page widget components.
// ABOUTME: Defines the contract that all widgets (node-list, node-editor, node-tree) must implement.

import { ReactElement } from 'react';

import { CustomPageFilter } from '../types';
import { parseDepthRange } from './utils/depthFilter';

export interface CardAction {
    key: string;
    label: string;
    icon: ReactElement;
    onClick: (nodeId: string) => void;
    /** Shown as an icon button on the card itself instead of inside its "more" menu. */
    inline?: boolean;
}

// React-router navigation state flag set when a detail widget navigates back to
// its list page. NodeListWidget reads it to suppress auto-open-single-result so
// the "back to list" path does not immediately bounce back into the detail view.
export const SKIP_AUTO_OPEN_STATE = { skipAutoOpen: true } as const;

export interface WidgetProps {
    hierarchyId: string;
    nodeId?: string;
    objectSchemaCode?: string;
    slug: string;
    filter?: CustomPageFilter;
    config?: Record<string, unknown>;
    /** Set on a `fullBleed` page: the widget fills the page height rather than sizing itself. */
    fillHeight?: boolean;
    /** Set on a `fullBleed` page, which shows no title of its own: the widget may show it. */
    pageTitle?: string;
}

export interface CardViewConfig {
    viewMode: 'card';
    maxItems: number;
    icon?: string;
    displayContentTypes?: string[];
    title?: string;
    showAddButton?: boolean;
    // Renders a search field above the cards that filters them by their displayed texts.
    showSearch: boolean;
    imageContentType?: string;
    cardVariant: 'standard' | 'highlight';
    direction: 'row' | 'column';
    cardWidth?: string;
    cardHeight?: string;
    cardBgColor?: string;
    cardTextColor?: string;
    // Renders the first two display content type values joined by a space on the title
    // line, so a card can show e.g. "first_name last_name" as one line instead of two.
    mergeTitle: boolean;
    // Content type holding the node's type, used to pick the fallback icon of a card
    // without an image. Defaults to the second display content type.
    typeContentType?: string;
}

const DEFAULT_MAX_ITEMS = 5;

interface CardViewDefaults {
    // Flow a widget falls back to when its config does not state one: favorite lists read as a
    // horizontal strip of shortcuts, while the other card lists stack vertically.
    direction?: 'row' | 'column';
}

export function parseCardViewConfig(
    config?: Record<string, unknown>,
    defaults?: CardViewDefaults,
): CardViewConfig | null {
    if (!config || config.viewMode !== 'card') return null;
    return {
        viewMode: 'card',
        maxItems:
            typeof config.maxItems === 'number' && config.maxItems > 0
                ? config.maxItems
                : DEFAULT_MAX_ITEMS,
        icon: typeof config.icon === 'string' ? config.icon : undefined,
        displayContentTypes: Array.isArray(config.displayContentTypes)
            ? (config.displayContentTypes as string[])
            : undefined,
        title: typeof config.title === 'string' ? config.title : undefined,
        showAddButton: typeof config.showAddButton === 'boolean' ? config.showAddButton : false,
        showSearch: config.showSearch === true,
        imageContentType:
            typeof config.imageContentType === 'string' ? config.imageContentType : undefined,
        cardVariant: config.cardVariant === 'highlight' ? 'highlight' : 'standard',
        direction:
            config.direction === 'row' || config.direction === 'column'
                ? config.direction
                : defaults?.direction ?? 'column',
        cardWidth: typeof config.cardWidth === 'string' ? config.cardWidth : undefined,
        cardHeight: typeof config.cardHeight === 'string' ? config.cardHeight : undefined,
        cardBgColor: typeof config.cardBgColor === 'string' ? config.cardBgColor : undefined,
        cardTextColor: typeof config.cardTextColor === 'string' ? config.cardTextColor : undefined,
        mergeTitle: config.mergeTitle === true,
        typeContentType:
            typeof config.typeContentType === 'string' ? config.typeContentType : undefined,
    };
}

export interface WidgetFrameConfig {
    title?: string;
    bgColor?: string;
    textColor?: string;
    maxHeight?: string;
    // Left undefined when the config does not mention it, so each widget can apply the
    // default that fits it (always on for dashboard-style widgets, off for full list pages).
    showViewAll?: boolean;
}

function parseCssSize(value: unknown): string | undefined {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) return `${value}px`;
    return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function parseNonEmptyString(value: unknown): string | undefined {
    return typeof value === 'string' && value.length > 0 ? value : undefined;
}

// Shared config of the widget frame: the section header title plus the styling of the
// content container that sits below it (see WidgetFrame).
export function parseWidgetFrameConfig(config?: Record<string, unknown>): WidgetFrameConfig {
    const title = parseNonEmptyString(config?.title);
    return {
        title: config?.showTitle !== false ? title : undefined,
        bgColor: parseNonEmptyString(config?.bgColor),
        textColor: parseNonEmptyString(config?.textColor),
        maxHeight: parseCssSize(config?.maxHeight),
        showViewAll: typeof config?.showViewAll === 'boolean' ? config.showViewAll : undefined,
    };
}

export interface ActionBarAction {
    type: string;
    contentTypeGroups?: string[];
    // Only for the "status" action: the workflowStatus content type to drive. Omitted when the
    // object schema has a single one, which the action auto-detects.
    contentTypeGroup?: string;
    contentType?: string;
}

export interface ActionBarConfig {
    actions: ActionBarAction[];
    backgroundColor: 'primary' | 'transparent';
    showTitle: boolean;
    titleContentTypeCode?: string;
}

export interface ChartWidgetConfig {
    chartType: 'bar' | 'pie' | 'line';
    dimensionContentType: { contentTypeGroup: string; contentType: string };
    measureContentType?: { contentTypeGroup: string; contentType: string };
    filters: Array<{ field: string; operator: string; value: string[] }>;
    title?: string;
    colorScheme?: string;
    hierarchyId?: string;
}

function isContentTypeRef(val: unknown): val is { contentTypeGroup: string; contentType: string } {
    if (typeof val !== 'object' || val === null) return false;
    const obj = val as Record<string, unknown>;
    return typeof obj.contentTypeGroup === 'string' && typeof obj.contentType === 'string';
}

export function parseChartWidgetConfig(config?: Record<string, unknown>): ChartWidgetConfig | null {
    if (!config) return null;
    const { dimensionContentType } = config;
    if (!isContentTypeRef(dimensionContentType)) return null;

    const chartType =
        config.chartType === 'pie' || config.chartType === 'line' ? config.chartType : 'bar';

    const rawFilters = Array.isArray(config.chartFilters) ? (config.chartFilters as unknown[]) : [];
    const filters = rawFilters.flatMap(raw => {
        if (typeof raw !== 'object' || raw === null) return [];
        const candidate = raw as { field?: unknown; operator?: unknown; value?: unknown };
        if (typeof candidate.field !== 'string' || typeof candidate.operator !== 'string')
            return [];
        return [
            {
                field: candidate.field,
                operator: candidate.operator,
                value: Array.isArray(candidate.value) ? (candidate.value as string[]) : [],
            },
        ];
    });

    return {
        chartType,
        dimensionContentType,
        measureContentType: isContentTypeRef(config.measureContentType)
            ? config.measureContentType
            : undefined,
        filters,
        title: typeof config.title === 'string' ? config.title : undefined,
        colorScheme: typeof config.colorScheme === 'string' ? config.colorScheme : undefined,
        hierarchyId: typeof config.hierarchyId === 'string' ? config.hierarchyId : undefined,
    };
}

export interface MatrixAxisConfig {
    hierarchyId: string;
    label?: string;
    depthRange?: number[];
    // Whether disabled nodes are loaded for this axis. Defaults to true (disabled nodes are shown, greyed out).
    includeDisabled: boolean;
    // Neo4j node property whose value is shown as the header label. Falls back to listName when unset.
    labelProperty?: string;
}

export interface MatrixWidgetConfig {
    rows: MatrixAxisConfig;
    columns: MatrixAxisConfig;
    cellIcon?: string;
    title?: string;
    // When true, an export button downloads the current matrix view as an XLSX file.
    exportEnabled: boolean;
    // Number of HAS_LINK_TO hops that constitutes a match (1 = direct link). Defaults to 1.
    linkDepth?: number;
}

function parseAxisConfig(value: unknown): MatrixAxisConfig | null {
    if (typeof value !== 'object' || value === null) return null;
    const axis = value as Record<string, unknown>;
    if (typeof axis.hierarchyId !== 'string' || axis.hierarchyId.length === 0) return null;
    return {
        hierarchyId: axis.hierarchyId,
        label: typeof axis.label === 'string' ? axis.label : undefined,
        depthRange: parseDepthRange(axis.depthRange),
        // Disabled nodes are included by default; only an explicit false excludes them.
        includeDisabled: axis.includeDisabled !== false,
        labelProperty:
            typeof axis.labelProperty === 'string' && axis.labelProperty.length > 0
                ? axis.labelProperty
                : undefined,
    };
}

export function parseMatrixWidgetConfig(
    config?: Record<string, unknown>,
): MatrixWidgetConfig | null {
    if (!config) return null;
    const rows = parseAxisConfig(config.rows);
    const columns = parseAxisConfig(config.columns);
    if (!rows || !columns) return null;

    return {
        rows,
        columns,
        cellIcon: typeof config.cellIcon === 'string' ? config.cellIcon : undefined,
        title: typeof config.title === 'string' ? config.title : undefined,
        // Export is opt-in: only an explicit true enables the button.
        exportEnabled: config.exportEnabled === true,
        linkDepth:
            typeof config.linkDepth === 'number' && Number.isFinite(config.linkDepth)
                ? config.linkDepth
                : undefined,
    };
}

export interface TextWidgetButton {
    label: string;
    link: string;
    openInNewTab?: boolean;
    variant: 'contained' | 'text';
    color: 'primary' | 'secondary';
    fullWidth: boolean;
}

export interface TextWidgetConfig {
    /** The paragraphs of the text: one per line of the configured text. */
    paragraphs: string[];
    buttons: TextWidgetButton[];
}

/**
 * Reads the text widget config. Blank lines are dropped; a button needs a label and a link.
 * Buttons default to contained primary, full width.
 */
export function parseTextWidgetConfig(config?: Record<string, unknown>): TextWidgetConfig {
    const text = typeof config?.text === 'string' ? config.text : '';
    const rawButtons = Array.isArray(config?.buttons) ? (config?.buttons as unknown[]) : [];
    const buttons: TextWidgetButton[] = rawButtons.flatMap(raw => {
        if (typeof raw !== 'object' || raw === null) return [];
        const candidate = raw as Record<string, unknown>;
        if (typeof candidate.label !== 'string' || !candidate.label) return [];
        if (typeof candidate.link !== 'string' || !candidate.link) return [];
        return [
            {
                label: candidate.label,
                link: candidate.link,
                openInNewTab: candidate.openInNewTab === true ? true : undefined,
                variant: candidate.variant === 'text' ? 'text' : 'contained',
                color: candidate.color === 'secondary' ? 'secondary' : 'primary',
                fullWidth: candidate.fullWidth !== false,
            },
        ];
    });
    return {
        paragraphs: text
            .split('\n')
            .map(line => line.trim())
            .filter(Boolean),
        buttons,
    };
}

export interface CardGridCard {
    title: string;
    icon?: string;
    image?: string;
    link?: string;
    openInNewTab?: boolean;
    bgColor?: string;
    textColor?: string;
}

export interface CardGridConfig {
    title?: string;
    columnsPerRow: number;
    cards: CardGridCard[];
}

const DEFAULT_COLUMNS_PER_ROW = 4;

export function parseCardGridConfig(config?: Record<string, unknown>): CardGridConfig | null {
    if (!config) return null;
    const rawCards = Array.isArray(config.cards) ? (config.cards as unknown[]) : [];
    const cards: CardGridCard[] = rawCards.flatMap(raw => {
        if (typeof raw !== 'object' || raw === null) return [];
        const candidate = raw as Record<string, unknown>;
        if (typeof candidate.title !== 'string' || candidate.title.length === 0) return [];
        return [
            {
                title: candidate.title,
                icon: typeof candidate.icon === 'string' ? candidate.icon : undefined,
                image: typeof candidate.image === 'string' ? candidate.image : undefined,
                link: typeof candidate.link === 'string' ? candidate.link : undefined,
                openInNewTab:
                    typeof candidate.openInNewTab === 'boolean'
                        ? candidate.openInNewTab
                        : undefined,
                bgColor: typeof candidate.bgColor === 'string' ? candidate.bgColor : undefined,
                textColor:
                    typeof candidate.textColor === 'string' ? candidate.textColor : undefined,
            },
        ];
    });
    if (cards.length === 0) return null;
    return {
        title: typeof config.title === 'string' ? config.title : undefined,
        columnsPerRow:
            typeof config.columnsPerRow === 'number' && config.columnsPerRow > 0
                ? config.columnsPerRow
                : DEFAULT_COLUMNS_PER_ROW,
        cards,
    };
}

export interface CompletenessWidgetConfig {
    hierarchyId?: string;
    rootNodeId?: string;
    countedFields: Array<{ contentTypeGroup: string; contentType: string }>;
    pageSize: number;
}

export interface TranslationCoverageWidgetConfig {
    hierarchyId?: string;
    rootNodeId?: string;
    languages?: string[];
    pageSize: number;
}

export interface ReviewPublishWidgetConfig {
    hierarchyId?: string;
    rootNodeId?: string;
    pageSize: number;
}

export const DEFAULT_COMPLETENESS_PAGE_SIZE = 25;

function parsePageSize(value: unknown): number {
    return typeof value === 'number' && Number.isFinite(value) && value > 0
        ? Math.floor(value)
        : DEFAULT_COMPLETENESS_PAGE_SIZE;
}

function parseCountedFields(value: unknown): Array<{
    contentTypeGroup: string;
    contentType: string;
}> {
    if (!Array.isArray(value)) return [];
    return value.flatMap(raw => (isContentTypeRef(raw) ? [raw] : []));
}

// The four completion-dashboard widgets share their branch scoping, so their configs are always
// valid: an omitted hierarchyId falls back to the page hierarchy and an omitted rootNodeId means
// the whole hierarchy.
export function parseCompletenessWidgetConfig(
    config?: Record<string, unknown>,
): CompletenessWidgetConfig {
    return {
        hierarchyId: parseNonEmptyString(config?.hierarchyId),
        rootNodeId: parseNonEmptyString(config?.rootNodeId),
        countedFields: parseCountedFields(config?.countedFields),
        pageSize: parsePageSize(config?.pageSize),
    };
}

export function parseTranslationCoverageConfig(
    config?: Record<string, unknown>,
): TranslationCoverageWidgetConfig {
    const languages = Array.isArray(config?.languages)
        ? (config?.languages as unknown[]).filter(
              (value): value is string => typeof value === 'string' && value.length > 0,
          )
        : undefined;
    return {
        hierarchyId: parseNonEmptyString(config?.hierarchyId),
        rootNodeId: parseNonEmptyString(config?.rootNodeId),
        languages: languages && languages.length > 0 ? languages : undefined,
        pageSize: parsePageSize(config?.pageSize),
    };
}

export function parseReviewPublishConfig(
    config?: Record<string, unknown>,
): ReviewPublishWidgetConfig {
    return {
        hierarchyId: parseNonEmptyString(config?.hierarchyId),
        rootNodeId: parseNonEmptyString(config?.rootNodeId),
        pageSize: parsePageSize(config?.pageSize),
    };
}

export function parseActionBarConfig(config?: Record<string, unknown>): ActionBarConfig {
    const rawActions = Array.isArray(config?.actions) ? (config?.actions as unknown[]) : [];
    const actions: ActionBarAction[] = rawActions.flatMap(raw => {
        if (typeof raw !== 'object' || raw === null) return [];
        const candidate = raw as {
            type?: unknown;
            contentTypeGroups?: unknown;
            contentTypeGroup?: unknown;
            contentType?: unknown;
        };
        if (typeof candidate.type !== 'string') return [];
        return [
            {
                type: candidate.type,
                contentTypeGroups: Array.isArray(candidate.contentTypeGroups)
                    ? (candidate.contentTypeGroups as string[])
                    : undefined,
                contentTypeGroup:
                    typeof candidate.contentTypeGroup === 'string'
                        ? candidate.contentTypeGroup
                        : undefined,
                contentType:
                    typeof candidate.contentType === 'string' ? candidate.contentType : undefined,
            },
        ];
    });
    const backgroundColor = config?.backgroundColor === 'transparent' ? 'transparent' : 'primary';
    const showTitle = config?.showTitle === true;
    const titleContentTypeCode =
        typeof config?.titleContentTypeCode === 'string' ? config.titleContentTypeCode : undefined;
    return { actions, backgroundColor, showTitle, titleContentTypeCode };
}
